import { useState, useEffect } from 'react';
import {
    Box, Typography, Paper, Button, TextField, CircularProgress,
    Alert, Stack, Switch, FormControlLabel
} from '@mui/material';
import { LicenseService } from '../../sdk/licenseService';
import { doc, setDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from '../../firebase';

const PLAN_KEYS = ['trial', 'basic', 'pro', 'community'];

const PlanDefinitionsManager = () => {
    const [authUser, setAuthUser] = useState(null);
    const [planDefs, setPlanDefs] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [successMsg, setSuccessMsg] = useState('');
    const [selectedPlan, setSelectedPlan] = useState('basic');
    const [editedPlan, setEditedPlan] = useState(null);

    useEffect(() => {
        const unsub = onAuthStateChanged(auth, u => setAuthUser(u || null));
        return () => unsub();
    }, []);

    useEffect(() => {
        const load = async () => {
            try {
                const defs = await LicenseService.getAllPlanDefinitions();
                setPlanDefs(defs);
                if (defs['basic']) setEditedPlan(JSON.parse(JSON.stringify(defs['basic'])));
            } catch (e) {
                setError('שגיאה בטעינת נתונים: ' + e.message);
            } finally {
                setLoading(false);
            }
        };
        load();
    }, []);

    const handleSelectPlan = (key) => {
        setSelectedPlan(key);
        if (planDefs?.[key]) {
            setEditedPlan(JSON.parse(JSON.stringify(planDefs[key])));
        } else {
            setEditedPlan({ displayName: key, limits: { screens: 1, savedWidgets: 0, versionHistory: 1, playlistsPerScreen: 2, dashboardsPerPlaylist: 2 }, features: { advancedDesign: false, prioritySupport: false, hardwareBundles: false } });
        }
        setSuccessMsg('');
        setError('');
    };

    const setLimit = (key, val) => {
        setEditedPlan(prev => ({ ...prev, limits: { ...prev.limits, [key]: Number(val) } }));
    };

    const setFeature = (key, val) => {
        setEditedPlan(prev => ({ ...prev, features: { ...prev.features, [key]: val } }));
    };

    const handleSavePlan = async () => {
        if (!editedPlan || !authUser) return;
        setSaving(true);
        setError('');
        setSuccessMsg('');
        try {
            await setDoc(doc(db, 'planDefinitions', selectedPlan), editedPlan, { merge: true });
            setPlanDefs(prev => ({ ...prev, [selectedPlan]: editedPlan }));
            setSuccessMsg(`מסלול "${selectedPlan}" עודכן בהצלחה!`);
        } catch (e) {
            setError('שגיאה בשמירה: ' + e.message);
        } finally {
            setSaving(false);
        }
    };

    if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>;

    return (
        <Box>
            <Typography variant="h6" fontWeight={700} color="#1e3a5f" mb={3}>
                עריכת הגדרות מסלולים
            </Typography>

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            {successMsg && <Alert severity="success" sx={{ mb: 2 }}>{successMsg}</Alert>}

            {/* Part A — Plan Editor */}
            <Paper sx={{ p: 3, mb: 3, borderRadius: '12px' }}>
                <Typography fontWeight={700} mb={2} fontSize="0.95rem">עריכת מסלול</Typography>

                {/* Plan selector tabs */}
                <Stack direction="row" gap={1} mb={3} flexWrap="wrap">
                    {PLAN_KEYS.map(k => (
                        <Button key={k} size="small" variant={selectedPlan === k ? 'contained' : 'outlined'}
                            onClick={() => handleSelectPlan(k)}
                            sx={{
                                textTransform: 'none',
                                fontWeight: 600,
                                fontSize: '0.8rem',
                                bgcolor: selectedPlan === k ? '#1e3a5f' : 'transparent',
                                borderColor: '#1e3a5f',
                                color: selectedPlan === k ? '#fff' : '#1e3a5f',
                                '&:hover': { bgcolor: selectedPlan === k ? '#152844' : 'rgba(30,58,95,0.06)' }
                            }}>
                            {planDefs?.[k]?.displayName || k}
                        </Button>
                    ))}
                </Stack>

                {editedPlan && (
                    <Stack gap={2.5}>
                        <TextField size="small" label="שם תצוגה" value={editedPlan.displayName || ''}
                            onChange={e => setEditedPlan(prev => ({ ...prev, displayName: e.target.value }))} />

                        <Typography fontWeight={600} fontSize="0.82rem" color="#64748b">מגבלות (9999 = ללא הגבלה)</Typography>
                        <Stack direction="row" gap={2}>
                            <TextField size="small" label="מסכים" type="number"
                                value={editedPlan.limits?.screens ?? ''} onChange={e => setLimit('screens', e.target.value)} sx={{ flex: 1 }} />
                            <TextField size="small" label="רכיבים שמורים" type="number"
                                value={editedPlan.limits?.savedWidgets ?? ''} onChange={e => setLimit('savedWidgets', e.target.value)} sx={{ flex: 1 }} />
                            <TextField size="small" label="גרסאות" type="number"
                                value={editedPlan.limits?.versionHistory ?? ''} onChange={e => setLimit('versionHistory', e.target.value)} sx={{ flex: 1 }} />
                        </Stack>
                        <Stack direction="row" gap={2}>
                            <TextField size="small" label="מערכי תצוגה למסך" type="number"
                                value={editedPlan.limits?.playlistsPerScreen ?? ''} onChange={e => setLimit('playlistsPerScreen', e.target.value)} sx={{ flex: 1 }} />
                            <TextField size="small" label="לוחות למערך תצוגה" type="number"
                                value={editedPlan.limits?.dashboardsPerPlaylist ?? ''} onChange={e => setLimit('dashboardsPerPlaylist', e.target.value)} sx={{ flex: 1 }} />
                        </Stack>

                        <Typography fontWeight={600} fontSize="0.82rem" color="#64748b">פיצ'רים</Typography>
                        <Stack direction="row" flexWrap="wrap" gap={1}>
                            {['advancedDesign', 'prioritySupport', 'hardwareBundles'].map(f => (
                                <FormControlLabel key={f} control={
                                    <Switch size="small" checked={!!editedPlan.features?.[f]}
                                        onChange={e => setFeature(f, e.target.checked)} />
                                } label={<Typography fontSize="0.82rem">{f}</Typography>} />
                            ))}
                        </Stack>

                        <Button variant="contained" onClick={handleSavePlan} disabled={saving}
                            sx={{ bgcolor: '#1e3a5f', '&:hover': { bgcolor: '#152844' }, textTransform: 'none', alignSelf: 'flex-start', px: 4 }}>
                            {saving ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : 'שמור מסלול'}
                        </Button>
                    </Stack>
                )}
            </Paper>

        </Box>
    );
};

export default PlanDefinitionsManager;
